import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Edit2, Trash2, User } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Review {
  id: string;
  content: string;
  rating: number;
  created_at: string;
  patient_id: string;
  profiles: {
    prenume: string;
    nume: string;
  } | null;
}

interface Profile {
  id: string;
  role: string;
}

interface DeleteConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteConfirmation({
  isOpen,
  onClose,
  onConfirm,
}: DeleteConfirmationProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Confirmare ștergere
        </h3>
        <p className="text-gray-600 mb-6">
          Sunteți sigur că doriți să ștergeți această recenzie? Această acțiune
          nu poate fi anulată.
        </p>
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition"
          >
            Anulează
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Șterge
          </button>
        </div>
      </div>
    </div>
  );
}

const ALLOWED_EMAILS = [
  'ovidiu.mudura@yahoo.com',
  'tudorbolos2001@yahoo.com',
  'mihaicojocarulaboncz@yahoo.com',
  'sergiumurg@yahoo.com',
  'sergiu.murg@decizieoncologica.ro',
  'viman.diana@yahoo.ro',
  'flaviuscoser@gmail.com',
];

export default function Reviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [newReview, setNewReview] = useState('');
  const [rating, setRating] = useState(5);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkUser();
    fetchReviews();
  }, []);

  const checkUser = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserEmail(user.email || null);
        setIsAuthorized(ALLOWED_EMAILS.includes(user.email || ''));

        const { data: profile } = await supabase
          .from('profiles')
          .select('id, role')
          .eq('id', user.id)
          .single();

        if (profile) {
          // If user is a doctor and not in allowed emails list, redirect to home
          if (
            profile.role === 'doctor' &&
            !ALLOWED_EMAILS.includes(user.email || '')
          ) {
            navigate('/');
            return;
          }

          setProfile(profile);
        }
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(
          `
          id,
          content,
          rating,
          created_at,
          patient_id,
          profiles (
            prenume,
            nume
          )
        `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to match Review interface
      const transformedData = (data || []).map((review: {
        id: string;
        content: string;
        rating: number;
        created_at: string;
        patient_id: string;
        profiles: { prenume: string; nume: string } | { prenume: string; nume: string }[];
      }) => ({
        ...review,
        profiles: Array.isArray(review.profiles)
          ? review.profiles[0]
          : review.profiles,
      }));

      setReviews(transformedData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError(
        error instanceof Error ? error.message : 'Error fetching reviews'
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    try {
      if (editingReview) {
        const { error } = await supabase
          .from('reviews')
          .update({
            content: newReview,
            rating,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingReview.id);

        if (error) throw error;
        setSuccess('Review updated successfully');
      } else {
        const { error } = await supabase.from('reviews').insert({
          patient_id: profile.id,
          content: newReview,
          rating,
        });

        if (error) throw error;
        setSuccess('Review added successfully');
      }

      setNewReview('');
      setRating(5);
      setEditingReview(null);
      await fetchReviews();
    } catch (error) {
      console.error('Error saving review:', error);
      setError('Error saving review');
    }

    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  };

  const handleDelete = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;
      await fetchReviews();
      setSuccess('Review deleted successfully');
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('Error deleting review');
    }

    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 3000);
  };

  const handleEdit = (review: Review) => {
    setEditingReview(review);
    setNewReview(review.content);
    setRating(review.rating);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Static Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Recenzii
          </h1>

          {profile?.role === 'pacient' && (
            <div className="mb-12">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRating(value)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-6 h-6 ${
                            value <= rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recenzia ta
                  </label>
                  <textarea
                    value={newReview}
                    onChange={(e) => setNewReview(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#024994] focus:border-[#024994]"
                    placeholder="Scrie-ți experiența aici..."
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="px-4 py-2 bg-[#024994] text-white rounded-lg hover:opacity-90 transition"
                >
                  {editingReview ? 'Actualizează recenzia' : 'Adaugă recenzia'}
                </button>
              </form>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          <div className="space-y-6">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-gray-50 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-5 h-5 ${
                                i < review.rating
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-5 h-5 text-gray-400" />
                        {review.profiles ? (
                          <h3 className="font-medium text-gray-900">
                            {review.profiles.prenume} {review.profiles.nume}
                          </h3>
                        ) : (
                          <span className="text-gray-500">Anonim</span>
                        )}
                      </div>
                      <p className="mt-2 text-gray-600">{review.content}</p>
                    </div>

                    {(profile?.id === review.patient_id || isAuthorized) && (
                      <div className="flex gap-2">
                        {profile?.id === review.patient_id && (
                          <button
                            onClick={() => handleEdit(review)}
                            className="p-2 text-blue-600 hover:text-blue-800 transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setReviewToDelete(review.id);
                            setShowDeleteConfirmation(true);
                          }}
                          className="p-2 text-red-600 hover:text-red-800 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">
                Nu există recenzii încă.
              </p>
            )}
          </div>
        </div>
      </div>

      {showDeleteConfirmation && (
        <DeleteConfirmation
          isOpen={showDeleteConfirmation}
          onClose={() => {
            setShowDeleteConfirmation(false);
            setReviewToDelete(null);
          }}
          onConfirm={() => {
            if (reviewToDelete) {
              handleDelete(reviewToDelete);
            }
          }}
        />
      )}
    </div>
  );
}
